import {
  Dispatch,
  KeyboardEvent,
  ReactNode,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

// Typeahead buffer. Module-level is fine: only one dropdown can hold focus at a time, and the
// buffer is cleared after SEARCH_RESET_DELAY of no typing anyway.
let searchString = '';
let searchTimeout: number = 0;

// How long a typed string keeps collecting letters, so that multiple-letter matches work
// like a native select
const SEARCH_RESET_DELAY = 500;

// How far PageUp/PageDown move the active option
const PAGE_JUMP = 10;

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
function filterOptions(options: string[] = [], filter: string, exclude: string[] = []) {
  return options.filter((option) => {
    const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
    return matches && exclude.indexOf(option) < 0;
  });
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
function getIndexByLetter(options: string[], filter: string, startIndex: number = 0) {
  const firstMatch = filterOptions(options, filter)[0];

  // first check if there is an exact match for the typed string
  if (firstMatch) {
    return options.indexOf(firstMatch);
  }

  const orderedOptions = [...options.slice(startIndex), ...options.slice(0, startIndex)];
  const allSameLetter = (letters: string[]) => letters.every((letter) => letter === letters[0]);

  // if the same letter is being repeated, cycle through first-letter matches
  if (allSameLetter(filter.split(''))) {
    const matches = filterOptions(orderedOptions, filter[0]);
    return options.indexOf(matches[0]);
  }

  // if no matches, return -1
  return -1;
}

function getSearchString(letter: string) {
  // reset typing timeout and start new timeout
  // this allows us to make multiple-letter matches, like a native select
  if (typeof searchTimeout === 'number') {
    window.clearTimeout(searchTimeout);
    searchTimeout = 0;
  }

  searchTimeout = window.setTimeout(() => {
    searchString = '';
    searchTimeout = 0;
  }, SEARCH_RESET_DELAY);

  // add most recent letter to saved search string
  searchString += letter;

  return searchString;
}

// What this hook reads off an option. Structural, so both dropdowns can pass their own.
type NavigableOption = { title?: ReactNode; value: string | number; disabled?: boolean };

// Lowercased text per option, for typeahead. A ReactNode title has no plain-text form, so
// fall back to the value.
function getSearchableOptionStrings(options: readonly NavigableOption[]) {
  return options.map((option) =>
    typeof option.title === 'string'
      ? option.title.toLowerCase()
      : option.value.toString().toLowerCase(),
  );
}

function clampIndex(index: number, optionCount: number) {
  if (index < 0) {
    return 0;
  }

  return index < optionCount ? index : optionCount - 1;
}

export type DropdownKeyboardNavigationParams = {
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled?: boolean;
  // Options in render order: typeahead matches them, their count clamps the active option
  options: readonly NavigableOption[];
  // The element wrapping the open popup, so the active option can be scrolled into view
  popupRef: RefObject<HTMLElement | null>;
  // Enter or Space on the active option while the dropdown is open. The dropdown is left open,
  // so a single-select has to close it here; a multiselect can stay open for the next toggle.
  onSelectActive: (index: number, event: KeyboardEvent<Element>) => void;
  // A typeahead match. The active option has already been moved to `index`, so this is only
  // for what a dropdown wants to do on top of that — commit the value, or open the popup.
  onTypeaheadMatch?: (index: number, event: KeyboardEvent<Element>) => void;
};

export type DropdownKeyboardNavigation = {
  activeOptionIndex: number | undefined;
  setActiveOptionIndex: Dispatch<SetStateAction<number | undefined>>;
  onKeyDown: (event: KeyboardEvent<Element>) => void;
};

/**
 * Keyboard interaction for a `role="combobox"` dropdown with a listbox popup: typeahead,
 * arrow/page/home/end navigation of the active option, Escape/Tab to close, and Enter/Space
 * to open or to act on the active option.
 *
 * Owns the active option, which is exposed to assistive tech through `aria-activedescendant`
 * on the combobox and marked on the options with `data-active` (queried here to keep it
 * scrolled into view).
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
 */
export default function useDropdownKeyboardNavigation({
  open,
  setOpen,
  disabled,
  options,
  popupRef,
  onSelectActive,
  onTypeaheadMatch,
}: DropdownKeyboardNavigationParams): DropdownKeyboardNavigation {
  const [activeOptionIndex, setActiveOptionIndex] = useState<number>();
  const optionCount = options.length;

  // Reset active option index when dropdown is closed
  useEffect(() => {
    if (!open) {
      setActiveOptionIndex(undefined);
    }
  }, [open]);

  // Scroll active option into view
  useEffect(() => {
    if (activeOptionIndex !== undefined) {
      const activeOption = popupRef.current?.querySelector('[data-active="true"]');
      activeOption?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeOptionIndex, popupRef]);

  const moveActiveOption = (delta: number) =>
    setActiveOptionIndex((prevIndex) =>
      clampIndex(prevIndex !== undefined ? prevIndex + delta : 0, optionCount),
    );

  const onKeyDown = (event: KeyboardEvent<Element>) => {
    if (disabled) {
      return; // a disabled dropdown ignores keys
    }

    const searchTexts = getSearchableOptionStrings(options);

    // A space belongs to an in-flight search only while it still narrows it down, so that
    // titles with spaces stay reachable without space losing its job of acting on the active
    // option — which is how a multiselect toggles.
    const isSearchedSpace =
      event.key === ' ' &&
      searchTimeout !== 0 &&
      filterOptions(searchTexts, `${searchString} `).length > 0;

    if (event.key.match(/^\p{L}$/u /* Unicode Letter, so never a space */) || isSearchedSpace) {
      if (isSearchedSpace) {
        event.preventDefault(); // space would scroll the page otherwise
      }

      const searchIndex = getIndexByLetter(
        searchTexts,
        getSearchString(event.key.toLowerCase()),
        (activeOptionIndex ?? -1) + 1,
      );

      if (searchIndex !== -1) {
        setActiveOptionIndex(searchIndex);
        onTypeaheadMatch?.(searchIndex, event);
      }
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      setOpen(false);
    } else if (event.key === 'Enter' || event.key === ' ' /* Space */) {
      event.preventDefault(); // Space will push down the viewport otherwise

      if (!open) {
        setOpen(true);
        // First option: the selected one in a single select, which sorts it to the front;
        // a multiselect has no single current value to start from.
        setActiveOptionIndex(0);
      } else if (activeOptionIndex !== undefined && !options.at(activeOptionIndex)?.disabled) {
        // Arrows can land on a disabled option; acting on it does nothing, and the popup
        // stays open because only onSelectActive closes it.
        onSelectActive(activeOptionIndex, event);
      }
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      moveActiveOption(event.key === 'ArrowUp' ? -1 : 1);
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      moveActiveOption(event.key === 'PageUp' ? -PAGE_JUMP : PAGE_JUMP);
    } else if (event.key === 'Home') {
      setActiveOptionIndex(0);
    } else if (event.key === 'End') {
      setActiveOptionIndex(optionCount - 1);
    }
  };

  return { activeOptionIndex, setActiveOptionIndex, onKeyDown };
}
